import React, { useState, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import { Dropdown } from './Dropdown';

/**
 * Props interface for Toolbar component
 */
interface ToolbarProps {
  /** TipTap editor instance */
  editor: Editor | null;
}

/**
 * Toolbar Component
 * Provides formatting controls for the rich text editor
 */
export const Toolbar: React.FC<ToolbarProps> = ({ editor }) => {
  // State for link input handling
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');

  if (!editor) {
    return null;
  }

  /**
   * Handles clicking on a link
   */
  const handleLinkClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const linkElement = target.closest('a');
    
    if (linkElement && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      window.open(linkElement.href, '_blank', 'noopener,noreferrer');
    }
  };

  /**
   * Handles adding or editing links
   */
  const addLink = () => {
    // Get selected text
    const selection = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(
      selection.from,
      selection.to,
      ' '
    );

    // Get current link if exists
    const previousUrl = editor.getAttributes('link').href || '';
    setLinkUrl(previousUrl);
    setLinkText(selectedText || '');
    setShowLinkInput(true);
  };

  /**
   * Saves the link to the editor
   */
  const saveLink = () => {
    if (linkUrl === '') {
      editor.chain().focus().unsetLink().run();
    } else {
      // Add protocol if not present
      const urlWithProtocol = linkUrl.match(/^https?:\/\//) ? linkUrl : `https://${linkUrl}`;
      
      // If there's no text selected, insert new text with link
      if (!editor.state.selection.content().size) {
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'text',
            text: linkText,
            marks: [{
              type: 'link',
              attrs: {
                href: urlWithProtocol,
                target: '_blank',
                rel: 'noopener noreferrer',
                class: 'cursor-pointer text-blue-600 hover:underline'
              }
            }]
          })
          .run();
      } else {
        // If text is selected, just add the link
        editor.chain().focus().setLink({ 
          href: urlWithProtocol,
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'cursor-pointer text-blue-600 hover:underline'
        }).run();
      }
    }
    setShowLinkInput(false);
    setLinkUrl('');
    setLinkText('');
  };

  /**
   * Toggles code block formatting
   */
  const toggleCodeBlock = () => {
    editor.chain().focus().toggleCodeBlock().run();
  };

  /**
   * Sets the font size for selected text
   */
  const setFontSize = (size: string) => {
    editor?.chain().focus().setMark('textStyle', { fontSize: size }).run();
  };

  // Keyboard shortcuts configuration
  const markdownShortcuts = [
    { key: 'b', command: () => editor.chain().focus().toggleBold().run() },
    { key: 'i', command: () => editor.chain().focus().toggleItalic().run() },
    { key: '`', command: () => editor.chain().focus().toggleCode().run() },
    { 
      key: 'k', 
      command: () => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);
        if (url === null) return;
        if (url === '') {
          editor.chain().focus().unsetLink().run();
          return;
        }
        editor.chain().focus().setLink({ href: url }).run();
      }
    },
  ];

  // Set up keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        markdownShortcuts.forEach(({ key, command }) => {
          if (e.key.toLowerCase() === key) {
            e.preventDefault();
            command();
          }
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editor]);

  // Add click handler to editor when mounted
  useEffect(() => {
    if (editor) {
      const editorElement = editor.view.dom;
      editorElement.addEventListener('click', handleLinkClick as any);
      
      return () => {
        editorElement.removeEventListener('click', handleLinkClick as any);
      };
    }
  }, [editor]);

  return (
    <div className="border-b border-gray-200 dark:border-gray-600">
      <div className="flex flex-wrap items-center divide-x divide-gray-200 dark:divide-gray-600">
        <div className="flex flex-wrap items-center divide-x divide-gray-200 dark:divide-gray-600 px-4">
          {/* Text Formatting */}
          <div className="flex items-center gap-1 pe-3 border-r border-gray-200 dark:border-gray-700">
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
                editor.isActive('bold') ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
              }`}
              title="Bold"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z"/>
              </svg>
            </button>

            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
                editor.isActive('italic') ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
              }`}
              title="Italic"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 4h4m-2 0v16m-8 0h16"/>
              </svg>
            </button>

            <button
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
                editor.isActive('underline') ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
              }`}
              title="Underline"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 4v7a6 6 0 0012 0V4M4 20h16"/>
              </svg>
            </button>

            <button
              onClick={() => editor.chain().focus().toggleStrike().run()}
              className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
                editor.isActive('strike') ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
              }`}
              title="Strike"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"/>
              </svg>
            </button>
          </div>

          {/* Bullet List */}
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-2 hover:text-gray-900 dark:hover:text-white ${
              editor.isActive('bulletList') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-400'
            }`}
            title="Bullet List"
          >
            <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M9 8h10M9 12h10M9 16h10M4.99 8H5m-.02 4h.01m0 4H5"/>
            </svg>
          </button>

          {/* Ordered List */}
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-2 hover:text-gray-900 dark:hover:text-white ${
              editor.isActive('orderedList') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-400'
            }`}
            title="Ordered List"
          >
            <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6h8m-8 6h8m-8 6h8M4 16a2 2 0 1 1 3.321 1.5L4 20h5M4 5l2-1v6m-2 0h4"/>
            </svg>
          </button>

          {/* Inline Code */}
          <button
            onClick={() => editor.chain().focus().toggleCode().run()}
            disabled={!editor.can().toggleCode()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
              editor.isActive('code') ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
            }`}
            title="Inline Code"
            type="button"
          >
            <svg 
              className="w-4 h-4" 
              aria-hidden="true" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24"
            >
              <path 
                stroke="currentColor" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="m8 8-4 4 4 4m8 0 4-4-4-4m-2-3-4 14"
              />
            </svg>
          </button>

          {/* Code Block */}
          <button
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            disabled={!editor.can().toggleCodeBlock()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
              editor.isActive('codeBlock') ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
            }`}
            title="Code Block"
            type="button"
          >
            <svg 
              className="w-4 h-4" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
              />
            </svg>
          </button>
        </div>

        {/* Alignment */}
        <div className="flex items-center gap-1 px-2 border-r border-gray-200 dark:border-gray-700">
          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
              editor.isActive({ textAlign: 'left' }) ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
            }`}
            title="Align Left"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path fillRule="evenodd" d="M3 5h18v2H3V5zm0 4h12v2H3V9zm0 4h18v2H3v-2zm0 4h12v2H3v-2z"/>
            </svg>
          </button>

          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
              editor.isActive({ textAlign: 'center' }) ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
            }`}
            title="Align Center"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path fillRule="evenodd" d="M3 5h18v2H3V5zm3 4h12v2H6V9zm-3 4h18v2H3v-2zm3 4h12v2H6v-2z"/>
            </svg>
          </button>

          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
              editor.isActive({ textAlign: 'right' }) ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
            }`}
            title="Align Right"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path fillRule="evenodd" d="M3 5h18v2H3V5zm6 4h12v2H9V9zm-6 4h18v2H3v-2zm6 4h12v2H9v-2z"/>
            </svg>
          </button>
        </div>

        {/* Link Button and Input */}
        <div className="relative">
          <button
            onClick={addLink}
            className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${
              editor.isActive('link') ? 'bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
            }`}
            title="Add Link"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" 
              />
            </svg>
          </button>

          {/* Link Input Popover */}
          {showLinkInput && (
            <div className="absolute left-0 mt-2 p-3 bg-white dark:bg-gray-700 rounded shadow-lg z-10 w-72">
              <div className="flex flex-col gap-2">
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Link text..."
                  className="px-2 py-1 text-sm border rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white w-full"
                />
                <input
                  type="text"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="Enter URL..."
                  className="px-2 py-1 text-sm border rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white w-full"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      saveLink();
                    }
                    if (e.key === 'Escape') {
                      setShowLinkInput(false);
                      setLinkUrl('');
                      setLinkText('');
                    }
                  }}
                />
                <div className="flex justify-end gap-2 mt-1">
                  <button
                    onClick={() => {
                      setShowLinkInput(false);
                      setLinkUrl('');
                      setLinkText('');
                    }}
                    className="px-2 py-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveLink}
                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Text Style Dropdowns */}
        <div className="flex items-center gap-1 px-2">
          <Dropdown
            trigger={
              <button
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                title="Text Size"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 7h6v2h-2v8h-2V9H9V7zm8 4h3v2h-3v3h-2v-3h-3v-2h3V8h2v3z"/>
                </svg>
              </button>
            }
            className="w-48 py-2 bg-white rounded-lg shadow-xl dark:bg-gray-800 border border-gray-200 dark:border-gray-600"
          >
            <div className="py-2">
              {[
                { size: '16px', label: 'Default' },
                { size: '12px', label: 'Tiny' },
                { size: '14px', label: 'Small' },
                { size: '18px', label: 'Lead' },
                { size: '24px', label: 'Large' },
                { size: '36px', label: 'Huge' },
              ].map(({ size, label }) => (
                <button
                  key={size}
                  onClick={() => setFontSize(size)}
                  className={`
                    w-full px-4 py-2 text-left text-sm inline-block
                    ${editor?.isActive('textStyle', { fontSize: size })
                      ? 'bg-gray-100 text-gray-900 dark:bg-gray-600 dark:text-white'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
                    }
                  `}
                  style={{ fontSize: size }}
                >
                  {label}
                </button>
              ))}
            </div>
          </Dropdown>

          <Dropdown
            trigger={
              <button
                className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                title="Text Color"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.246 14H8.754l-1.6 4H5l7-18h2l7 18h-2.154l-1.6-4zm-.8-2L12 5.885 9.554 12h4.892z"/>
                </svg>
              </button>
            }
            className="w-64 py-2 bg-white rounded-lg shadow-xl dark:bg-gray-800 border border-gray-200 dark:border-gray-600"
          >
            <div className="p-3">
              <div className="grid grid-cols-5 gap-2">
                {[
                  '#000000', '#e60000', '#ff9900', '#ffff00', '#008a00',
                  '#0066cc', '#9933ff', '#ffffff', '#facccc', '#ffebcc',
                  '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff', '#bbbbbb',
                  '#f06666', '#ffc266', '#ffff66', '#66b966', '#66a3e0'
                ].map((color) => (
                  <button
                    key={color}
                    onClick={() => editor.chain().focus().setColor(color).run()}
                    className="w-6 h-6 rounded border border-gray-200 dark:border-gray-600"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
              <button
                onClick={() => editor.chain().focus().unsetColor().run()}
                className="w-full mt-2 px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
              >
                Reset color
              </button>
            </div>
          </Dropdown>
        </div>
      </div>
    </div>
  );
}; 